import type { PaginationState, SortingState } from '@tanstack/react-table';
import { create } from 'zustand';

import type { ConnectorCodeWire } from '~/features/catalog-operations/types/catalog-operations.api.types';
import type { ArchiveView } from '~/features/list-archive/types/list-archive.types';

/** Committed filter-drawer selections — mirrors CatalogFilterApplyPayload's fields. */
interface CatalogOperationsFilters {
  connectorCode: ConnectorCodeWire | undefined;
  useCase: string | undefined;
  fromDate: string | undefined;
  toDate: string | undefined;
  coverage: string | undefined;
  /** Batch trigger source — maps to `?source=…` on `/jobs`
   *  (e.g. `SCHEDULED`, `SCHEDULED_UX_TRIGGER`, `TRIAL`, `FEED_AUDIT`). */
  source: string | undefined;
  appliedFilterCount: number;
}

interface CatalogOperationsState {
  activeTab: string;
  /** Admin-only Active | Archived view (List Archive). Orthogonal to `activeTab`:
   *  the status tabs keep filtering inside whichever view is selected. */
  view: ArchiveView;
  /** Committed search only — the in-flight draft stays local component state. */
  search: string;
  filters: CatalogOperationsFilters;
  /** 0-based, mirrors TanStack's PaginationState exactly (no 1-based translation). */
  pagination: PaginationState;
  sorting: SortingState;
}

interface CatalogOperationsActions {
  /** Switches tabs. Resets committed search and pagination to page 1 (new tab
   *  = new result set) but preserves filters and sort. */
  setActiveTab: (tab: string) => void;
  /** Switches between the Active and Archived views. Resets pagination to
   *  page 1 (different result set) but keeps tab, search, filters and sort —
   *  the archived filter composes with all of them server-side. */
  setView: (view: ArchiveView) => void;
  /** Commits a search term (fires the API request). Resets pagination to
   *  page 1 since the result set changes underneath the current page. */
  setSearch: (search: string) => void;
  /** Commits the filter-drawer payload — used for both Apply and Reset Filters
   *  (Reset sends the same shape with every field back to its default/0).
   *  Resets pagination to page 1 for the same reason as setSearch. */
  applyFilters: (payload: CatalogOperationsFilters) => void;
  /** Updates DataTable-controlled pagination (page or page-size change). */
  setPagination: (pagination: PaginationState) => void;
  /** Updates DataTable-controlled sort state. */
  setSorting: (sorting: SortingState) => void;
  /** Restores full initial state. */
  reset: () => void;
}

const INITIAL_FILTERS: CatalogOperationsFilters = {
  connectorCode: undefined,
  useCase: undefined,
  fromDate: undefined,
  toDate: undefined,
  coverage: undefined,
  source: undefined,
  appliedFilterCount: 0,
};

/** pageSize: 10 matches CatalogBatchTable's DataTable pageSize. */
const INITIAL_STATE: CatalogOperationsState = {
  activeTab: 'all',
  view: 'active',
  search: '',
  filters: INITIAL_FILTERS,
  pagination: { pageIndex: 0, pageSize: 10 },
  sorting: [],
};

/** In-memory (not persisted) store for the Catalog Operations listing page's
 *  filter/search/pagination/sort state — survives SPA navigation to a batch
 *  detail / trial flow and back. Resets naturally on tenant switch (full
 *  page reload re-creates every store) and on tab close. */
export const useCatalogOperationsStore = create<
  CatalogOperationsState & CatalogOperationsActions
>()((set) => ({
  ...INITIAL_STATE,

  setActiveTab: (tab) => {
    set((s) => ({
      activeTab: tab,
      search: '',
      pagination: { ...s.pagination, pageIndex: 0 },
    }));
  },

  setView: (view) => {
    set((s) => ({ view, pagination: { ...s.pagination, pageIndex: 0 } }));
  },

  setSearch: (search) => {
    set((s) => ({ search, pagination: { ...s.pagination, pageIndex: 0 } }));
  },

  applyFilters: (payload) => {
    set((s) => ({ filters: payload, pagination: { ...s.pagination, pageIndex: 0 } }));
  },

  setPagination: (pagination) => {
    set({ pagination });
  },

  setSorting: (sorting) => {
    set({ sorting });
  },

  reset: () => {
    set(INITIAL_STATE);
  },
}));
